import { isEmpty, isNil } from 'lodash';
import { CSSProperties, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { AnchoredSheet } from '@shared/components/AnchoredSheet';
import { DateRangePicker } from '@shared/components/DateRangePicker';
import { View } from '@shared/components/View';
import { DateField } from '@shared/data/entity/fields/DateField';
import { DateRangeField } from '@shared/data/entity/fields/DateRangeField';
import { FormattedDateRange } from '@shared/scenes/cards/collectionView/components/fields/DateRangeCollectionViewField';
import { UpdateField } from '@shared/scenes/entity/types';
import { getComponentLabel, getComponentPlaceholder } from '@shared/scenes/workflows/utils';
import { getAnchorName } from '@shared/styles';
import { API } from '@shared/types/api';
import { formatDateTimeWithYear } from '@shared/util/date';
import { useBoolean } from '@shared/util/hooks';
import { EditFieldContainer } from './EditFieldContainer';
import styles from './DateRangeEditField.module.css';

interface Props {
  field: DateRangeField | null;
  startDateField: DateField | null;
  endDateField: DateField | null;
  updateField: UpdateField;
  component: API.DateRangeComponent;
  error?: string;
  style?: CSSProperties;
}

export function DateRangeEditField({
  field,
  startDateField,
  endDateField,
  updateField,
  component,
  error,
  style,
}: Props) {
  const componentId = component.id;
  const anchorName = getAnchorName('date-range-edit', component.id);
  const { startDateComponentId, endDateComponentId } = component;
  const isReadOnly = 'readonly' in component && !!component.readonly;

  const [isPickerOpen, openPicker, closePicker] = useBoolean(false);
  const startDate = startDateField?.date;
  const endDate = endDateField?.date;

  const showError = !isEmpty(error);
  const hasValue = !isNil(field) && !field.isEmpty;
  const showClearButton = hasValue && !isReadOnly;
  const placeholder = getComponentPlaceholder(component);

  // Each edge writes only its own end of the range, reading its sibling from the record rather
  // than from this render. The picker sets both edges in the same tick, so a setter that also
  // rewrote its sibling from a stale closure would undo the other's write.
  const writeEdge = useCallback(
    (edge: 'start' | 'end', date?: string) => {
      updateField(edge === 'start' ? startDateComponentId : endDateComponentId, () =>
        DateField.fromFieldJson(date),
      );
      updateField(componentId, previousField => {
        const previousRange =
          previousField instanceof DateRangeField
            ? previousField.dateRange
            : { start: null, end: null };
        return DateRangeField.fromFieldJson({ ...previousRange, [edge]: date });
      });
    },
    [updateField, componentId, startDateComponentId, endDateComponentId],
  );

  const setStartDate = useCallback((start?: string) => writeEdge('start', start), [writeEdge]);
  const setEndDate = useCallback((end?: string) => writeEdge('end', end), [writeEdge]);

  const onClear = useCallback(() => {
    writeEdge('start', undefined);
    writeEdge('end', undefined);
  }, [writeEdge]);

  const printContent = hasValue
    ? [
        field.dateRange.start ? formatDateTimeWithYear(field.dateRange.start) : null,
        field.dateRange.end ? formatDateTimeWithYear(field.dateRange.end) : null,
      ]
        .filter(Boolean)
        .join(' \u2013 ') || undefined
    : undefined;

  return (
    <EditFieldContainer
      component={component}
      showClearButton={showClearButton}
      showPlaceholder={false}
      onClear={onClear}
      error={showError ? error : undefined}
      anchorName={anchorName}
      onPress={isReadOnly ? undefined : openPicker}
      isReadOnly={isReadOnly}
      includeWrapperPadding={false}
      style={style}
      printContent={printContent}
    >
      <DateRangeContent isReadOnly={isReadOnly} field={field} placeholder={placeholder} />
      {isPickerOpen &&
        createPortal(
          <AnchoredSheet
            anchorName={anchorName}
            onClose={closePicker}
            initialSize="full"
            aria-label={getComponentLabel(component)}
          >
            <View padding={1}>
              <DateRangePicker
                startDateStamp={startDate}
                endDateStamp={endDate}
                setStartDateStamp={setStartDate}
                setEndDateStamp={setEndDate}
              />
            </View>
          </AnchoredSheet>,
          document.body,
        )}
    </EditFieldContainer>
  );
}

interface DateRangeContentProps {
  isReadOnly: boolean;
  field: DateRangeField | null;
  placeholder: string | undefined;
}

function DateRangeContent({ isReadOnly, field, placeholder }: DateRangeContentProps) {
  const hasValue = !isNil(field) && !field.isEmpty;

  return (
    <View className={[styles.fieldContent, isReadOnly && styles.readonly]}>
      {hasValue && <FormattedDateRange field={field} />}
      {!hasValue && <View className={styles.placeholder}>{placeholder}</View>}
      <View />
    </View>
  );
}
